import { useState, useEffect, useMemo } from 'react';
import { fetchData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { Fade, TextField, InputAdornment, Button, Paper } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Pagination from '../../common/Pagination';
import LogoLoader from '../../common/LogoLoader';
import * as XLSX from 'xlsx-js-style';

function DebtorsListReport() {
  useSessionValidation();

  const { companyId, branchId, token, companyName } = useAuth();

  const today = new Date().toISOString().split('T')[0];

  const [mounted, setMounted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [toDate, setToDate] = useState(today);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => { setMounted(true); }, []);

  const fetchDebtors = async () => {
    if (!companyId || !token) return;
    setLoading(true);
    try {
      const response = await fetchData(
        'ReportApi',
        `/DebtorsList/${companyId}/${branchId || 0}/${toDate}/${toDate}`,
        token
      );
      const rows = response?.response_data || response || [];
      setData(Array.isArray(rows) ? rows : []);
    } catch (e) {
      console.error('Error fetching debtors list:', e);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyId && token) fetchDebtors();
  }, [companyId, token]); // eslint-disable-line

  const fmt = (n) =>
    parseFloat(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const filteredData = useMemo(() =>
    data.filter(item =>
      !searchTerm ||
      (item.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(item.Billno || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.billdate || '').toLowerCase().includes(searchTerm.toLowerCase())
    ),
    [data, searchTerm]
  );

  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const paginatedData = filteredData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const totalAmount = useMemo(() =>
    filteredData.reduce((sum, item) => sum + Math.abs(parseFloat(item.amount || 0)), 0),
    [filteredData]
  );

  const handleExportExcel = () => {
    const wb = XLSX.utils.book_new();
    const COLS = 6;

    const aoa = [
      [companyName || 'Debtors List', ...Array(COLS - 1).fill('')],
      ['Debtors List', ...Array(COLS - 1).fill('')],
      [`As of : ${toDate}`, ...Array(COLS - 1).fill('')],
      Array(COLS).fill(''),
      ['#', 'Name', 'Bill No', 'Bill Date', 'Amount', 'Days'],
      ...filteredData.map((item, i) => [
        item.slno || i + 1,
        item.name || '',
        item.Billno || '',
        item.billdate ? new Date(item.billdate).toLocaleDateString('en-IN') : '',
        Math.abs(parseFloat(item.amount || 0)),
        item.days || 0,
      ]),
      [filteredData.length, 'Total', '', '', totalAmount, ''],
    ];

    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = [{ wch: 6 }, { wch: 28 }, { wch: 14 }, { wch: 14 }, { wch: 16 }, { wch: 8 }];
    ws['!rows'] = [{ hpt: 22 }, { hpt: 18 }, { hpt: 16 }, { hpt: 6 }, { hpt: 20 }];
    ws['!merges'] = [0, 1, 2, 3].map(r => ({ s: { r, c: 0 }, e: { r, c: COLS - 1 } }));

    const infoS = (sz, bold, color = '5c3d1e') => ({
      font: { bold, sz, color: { rgb: color } },
      alignment: { horizontal: 'center', vertical: 'center' },
      fill: { patternType: 'solid', fgColor: { rgb: 'FFF8EE' } },
    });
    ws['A1'].s = infoS(16, true);
    ws['A2'].s = infoS(13, true, '2d3748');
    ws['A3'].s = infoS(11, false, '444444');

    const hdrS = {
      font: { bold: true, sz: 11, color: { rgb: 'FFFFFF' } },
      fill: { patternType: 'solid', fgColor: { rgb: '5c3d1e' } },
      alignment: { horizontal: 'center', vertical: 'center' },
      border: {
        top:    { style: 'medium', color: { rgb: '3b2410' } },
        bottom: { style: 'medium', color: { rgb: '3b2410' } },
        left:   { style: 'thin',   color: { rgb: '3b2410' } },
        right:  { style: 'thin',   color: { rgb: '3b2410' } },
      },
    };
    for (let c = 0; c < COLS; c++) {
      const addr = XLSX.utils.encode_cell({ r: 4, c });
      if (ws[addr]) ws[addr].s = hdrS;
    }

    const numericCols = new Set([4, 5]);
    filteredData.forEach((_, i) => {
      const even = i % 2 === 0;
      for (let c = 0; c < COLS; c++) {
        const addr = XLSX.utils.encode_cell({ r: 5 + i, c });
        if (!ws[addr]) ws[addr] = { t: 's', v: '' };
        ws[addr].s = {
          font: { sz: 10, color: { rgb: '2d3748' } },
          fill: { patternType: 'solid', fgColor: { rgb: even ? 'FFFFFF' : 'FDF6EC' } },
          alignment: { horizontal: numericCols.has(c) ? 'right' : 'left', vertical: 'center' },
          border: {
            top:    { style: 'thin', color: { rgb: 'E0E0E0' } },
            bottom: { style: 'thin', color: { rgb: 'E0E0E0' } },
            left:   { style: 'thin', color: { rgb: 'E0E0E0' } },
            right:  { style: 'thin', color: { rgb: 'E0E0E0' } },
          },
        };
      }
    });

    // Total row styling in Excel
    const totalRowIdx = 5 + filteredData.length;
    for (let c = 0; c < COLS; c++) {
      const addr = XLSX.utils.encode_cell({ r: totalRowIdx, c });
      if (!ws[addr]) ws[addr] = { t: 's', v: '' };
      ws[addr].s = {
        font: { bold: true, sz: 12, color: { rgb: 'BF360C' } },
        fill: { patternType: 'solid', fgColor: { rgb: 'FFF3E0' } },
        alignment: { horizontal: c === 4 ? 'right' : c === 0 ? 'center' : 'left', vertical: 'center' },
        border: {
          top:    { style: 'medium', color: { rgb: 'E65100' } },
          bottom: { style: 'medium', color: { rgb: 'E65100' } },
          left:   { style: 'thin',   color: { rgb: 'E65100' } },
          right:  { style: 'thin',   color: { rgb: 'E65100' } },
        },
      };
    }

    XLSX.utils.book_append_sheet(wb, ws, 'Debtors');
    XLSX.writeFile(wb, `DebtorsList_${new Date().toISOString().split('T')[0]}.xlsx`, { bookSST: false, cellStyles: true });
  };

  const headerCell = (align = 'left') => ({
    color: 'white', fontWeight: '600', fontSize: '11px',
    textTransform: 'uppercase', letterSpacing: '0.5px',
    padding: '4px 10px', textAlign: align,
    whiteSpace: 'nowrap', backgroundColor: '#5c3d1e',
  });

  const bodyCell = (align = 'left', bold = false) => ({
    fontSize: '12px', padding: '6px 10px',
    color: '#2d3748', whiteSpace: 'nowrap',
    textAlign: align, fontWeight: bold ? '600' : '400',
  });

  const pageStartIndex = (currentPage - 1) * itemsPerPage;

  if (!mounted) return null;

  return (
    <Fade in={mounted} timeout={800}>
      <div style={{ minWidth: '100%', background: '#fdf6ec', padding: '10px', position: 'relative' }}>
        {loading && <LogoLoader open={loading} message="Loading Debtors List..." />}

        <div style={{
          margin: '0 auto', background: 'white', borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)', padding: '12px',
          color: 'black', fontFamily: 'Times New Roman', overflow: 'hidden',
        }}>
          {/* Title */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', paddingBottom: '8px', borderBottom: '1px solid #f0f0f0' }}>
            <h4 style={{ fontSize: '17px', fontWeight: '700', margin: 0, letterSpacing: '-0.3px', fontFamily: 'Times New Roman', textTransform: 'uppercase' }}>
              <span style={{ color: 'black' }}>📋 Debtors List</span>
            </h4>
          </div>

          {/* Toolbar */}
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap' }}>
            <label style={{ fontSize: '12px', fontWeight: '600', color: '#555', whiteSpace: 'nowrap' }}>To Date</label>
            <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
              style={{ border: '1px solid #c9a84c', borderRadius: '6px', padding: '3px 8px', fontSize: '12px', height: '29px', background: '#fdf6ec', color: '#333', outline: 'none', cursor: 'pointer' }} />

            <Button variant="contained" onClick={() => { setCurrentPage(1); fetchDebtors(); }}
              style={{
                background: 'linear-gradient(135deg, #388e3c 0%, #43a047 100%)',
                color: 'white', padding: '4px 16px', borderRadius: '6px',
                fontSize: '12px', fontWeight: '700', border: 'none',
                minHeight: '29px', textTransform: 'none', whiteSpace: 'nowrap',
                boxShadow: '0 2px 8px rgba(56,142,60,0.4)',
              }}>
              Refresh
            </Button>

            <Button variant="contained" onClick={handleExportExcel} disabled={filteredData.length === 0}
              style={{
                background: filteredData.length === 0 ? '#ccc' : 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)',
                color: 'white', padding: '4px 16px', borderRadius: '6px',
                fontSize: '12px', fontWeight: '700', border: 'none',
                minHeight: '29px', textTransform: 'none', whiteSpace: 'nowrap',
                boxShadow: filteredData.length === 0 ? 'none' : '0 2px 8px rgba(21,101,192,0.4)',
              }}>
              📥 Excel
            </Button>

            <div style={{ flex: 1 }} />

            <TextField size="small" placeholder="Search name, bill no..." value={searchTerm}
              onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon style={{ color: '#c9a84c', fontSize: '18px' }} /></InputAdornment> }}
              sx={{ minWidth: '200px', '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: '#fdf6ec', fontSize: '13px', height: '29px', '&:hover fieldset': { borderColor: '#c9a84c' }, '&.Mui-focused fieldset': { borderColor: '#c9a84c' } } }} />
          </div>

          {/* Table */}
          <div style={{ marginBottom: '10px' }}>
            <style>{`
              .debtors-scroll::-webkit-scrollbar { height: 10px; width: 8px; }
              .debtors-scroll::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 4px; }
              .debtors-scroll::-webkit-scrollbar-thumb { background: #c9a84c; border-radius: 4px; }
              .debtors-scroll::-webkit-scrollbar-thumb:hover { background: #5c3d1e; }
              .debtors-scroll { scrollbar-width: thin; scrollbar-color: #c9a84c #f1f1f1; }
            `}</style>
            <div className="debtors-scroll" style={{ overflowX: 'scroll', overflowY: 'auto', maxHeight: 'calc(100vh - 230px)', borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
              <TableContainer component={Paper} sx={{ boxShadow: 'none', borderRadius: 0, overflow: 'visible' }}>
                <Table stickyHeader sx={{ minWidth: 700 }} aria-label="debtors table">
                  <TableHead>
                    <TableRow style={{ height: '27px' }}>
                      <TableCell style={headerCell('center')}>#</TableCell>
                      <TableCell style={headerCell()}>Name</TableCell>
                      <TableCell style={headerCell()}>Bill No</TableCell>
                      <TableCell style={headerCell()}>Bill Date</TableCell>
                      <TableCell style={headerCell('right')}>Amount</TableCell>
                      <TableCell style={headerCell('center')}>Days</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedData.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} style={{ textAlign: 'center', padding: '24px', color: '#999', fontSize: '13px' }}>
                          {loading ? 'Loading...' : 'No records found.'}
                        </TableCell>
                      </TableRow>
                    ) : (
                        paginatedData.map((item, index) => {
                          const globalIndex = pageStartIndex + index;
                          const rowBg = index % 2 === 0 ? '#ffffff' : '#fdf6ec';
                          const billDate = item.billdate
                            ? new Date(item.billdate).toLocaleDateString('en-IN')
                            : '—';
                          const days = item.days || 0;
                          // Color-code days: green < 30, orange 30-60, red > 60
                          const daysColor = days > 60 ? '#c62828' : days > 30 ? '#e65100' : '#2e7d32';

                          return (
                            <TableRow key={globalIndex} style={{ background: rowBg, borderBottom: '1px solid #f0f0f0' }}>
                              <TableCell style={{ ...bodyCell('center'), color: '#888' }}>{item.slno || globalIndex + 1}</TableCell>
                              <TableCell style={{ ...bodyCell(), fontWeight: '500' }}>{item.name || '—'}</TableCell>
                              <TableCell style={{ ...bodyCell('left', true), color: '#5c3d1e' }}>{item.Billno || '—'}</TableCell>
                              <TableCell style={bodyCell()}>{billDate}</TableCell>
                              <TableCell style={{ ...bodyCell('right', true), color: '#1565c0' }}>{fmt(item.amount)}</TableCell>
                              <TableCell style={{ ...bodyCell('center', true), color: daysColor }}>{days}</TableCell>
                            </TableRow>
                          );
                        })
                    )}
                    {filteredData.length > 0 && (
                      <TableRow style={{ background: '#fff3e0', borderTop: '2px solid #e65100' }}>
                        <TableCell style={{ fontSize: '13px', padding: '7px 10px', fontWeight: '800', color: '#bf360c', textAlign: 'center' }}>{filteredData.length}</TableCell>
                        <TableCell colSpan={3} style={{ fontSize: '13px', padding: '7px 10px', fontWeight: '800', color: '#bf360c', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total</TableCell>
                        <TableCell style={{ fontSize: '13px', padding: '7px 10px', fontWeight: '800', color: '#bf360c', textAlign: 'right' }}>{fmt(totalAmount)}</TableCell>
                        <TableCell style={{ fontSize: '13px', padding: '7px 10px', fontWeight: '800', color: '#bf360c', textAlign: 'center' }}>—</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </div>
          </div>

          <Pagination
            currentPage={currentPage} totalPages={totalPages}
            totalItems={totalItems} itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={n => { setItemsPerPage(n); setCurrentPage(1); }}
            pageSizeOptions={[10, 25, 50, 100]}
          />
        </div>
      </div>
    </Fade>
  );
}

export default DebtorsListReport;
